"use client";

import { useEffect, useRef, useState } from "react";
import { SectionBadge } from "./SectionBadge";
import { useI18n } from "@/i18n/context";

export function ContextSection() {
  const { t } = useI18n();
  const sectionRef = useRef<HTMLElement>(null);
  const [entered, setEntered] = useState(false);
  const [rotation, setRotation] = useState(0);

  useEffect(() => {
    // Entrance detection
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setEntered(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);

    // Scroll-driven rotation
    const onScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const vh = window.innerHeight;

      // Only compute when section is visible
      if (rect.bottom < 0 || rect.top > vh) return;

      // Progress: 0 (section just entering) → 1 (section leaving)
      const progress = 1 - (rect.bottom / (vh + rect.height));
      setRotation(progress * 90); // 90 degrees total over the section
    };

    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  return (
    <section
      id="context"
      ref={sectionRef}
      className="relative h-[900px] bg-bg-teal overflow-hidden flex flex-col items-center"
    >
      {/* Concentric circles — rasterized PNG for cheap GPU rotation */}
      <div className="absolute inset-[-40%] flex items-center justify-center pointer-events-none">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="/images/circles.svg"
          alt=""
          aria-hidden="true"
          className={`min-w-full min-h-full object-cover ${
            entered ? "opacity-100" : "opacity-0"
          }`}
          style={{
            transform: `rotate(${entered ? rotation : -30}deg)`,
            transition: entered ? "none" : "opacity 1.2s cubic-bezier(0.16,1,0.3,1), transform 1.2s cubic-bezier(0.16,1,0.3,1)",
            willChange: "transform",
          }}
        />
      </div>

      {/* Badge */}
      <div className="relative z-10 mt-[55px]" data-reveal>
        <SectionBadge>{t.context.badge}</SectionBadge>
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center flex-1 max-w-[776px] px-8">
        <h2 data-reveal className="font-clash font-bold text-[38px] text-text-primary text-center uppercase tracking-[2.66px] leading-normal mb-[40px]">
          {t.context.title}
        </h2>
        <p data-reveal className="font-besley text-[24px] text-text-secondary text-center tracking-[1.68px] leading-[28px]">
          {t.context.body}
        </p>
      </div>
    </section>
  );
}
